import { isEmpty, isEqual } from 'lodash';
import {
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { API } from '@shared/types/api';
import { createCtx } from '@shared/util/context';
import { useStableValue } from '@shared/util/hooks';
import { omitTypeName } from '@shared/util/omitTypeName';
import {
  EditableViewTemplate,
  PreserveViewTemplate,
  getEditableViewTemplate,
} from './PreserveViewTemplate';
import { useSaveViewTemplate } from './useSaveViewTemplate';

export interface ViewTemplateState {
  viewTemplate: API.ViewTemplateFragment;
  savedViewTemplate: API.ViewTemplateFragment;
  isModified?: boolean;
}

export interface ViewTemplateActions {
  setViewTemplate: Dispatch<SetStateAction<API.ViewTemplateFragment>>;
  revertViewTemplate: VoidFunction;
  saveViewTemplate: () => Promise<void>;
}

const [useViewTemplateState, ViewTemplateStateProvider] = createCtx<ViewTemplateState>();
const [useViewTemplateActions, ViewTemplateActionsProvider] = createCtx<ViewTemplateActions>();
export { useViewTemplateState, useViewTemplateActions };

interface ViewTemplateProviderProps {
  children: ReactNode;
  workflowTemplateId: string;
  viewTemplate: API.ViewTemplateFragment;
}

export function ViewTemplateProvider({
  children,
  workflowTemplateId,
  viewTemplate: inputViewTemplate,
}: ViewTemplateProviderProps) {
  const [savedViewTemplate, setSavedViewTemplate] = useStableValue(omitTypeName(inputViewTemplate));
  const [viewTemplate, setViewTemplate] = useStableValue(savedViewTemplate);
  const saveViewTemplate = useSaveViewTemplate(workflowTemplateId, setViewTemplate);
  const [isModified, setIsModified] = useState(false);

  useEffect(() => {
    // reset savedViewTemplate when inputViewTemplate changes
    setSavedViewTemplate(omitTypeName(inputViewTemplate));
  }, [inputViewTemplate, setSavedViewTemplate]);

  useEffect(() => {
    // update viewTemplate when editable parts of savedViewTemplate change
    setViewTemplate(prev => {
      const updates = getViewTemplateUpdates(savedViewTemplate, prev);
      if (isEmpty(updates)) {
        return prev;
      } else {
        return { ...prev, ...updates };
      }
    });
  }, [savedViewTemplate, setViewTemplate]);

  const revertViewTemplate = useCallback(() => {
    // accessing current savedViewTemplate via state-setter, so this callback
    // can be stateless
    setSavedViewTemplate(saved => {
      setViewTemplate(saved);
      return saved;
    });
  }, [setViewTemplate, setSavedViewTemplate]);

  const actions = useMemo(
    () => ({ setViewTemplate, revertViewTemplate, saveViewTemplate }),
    [setViewTemplate, revertViewTemplate, saveViewTemplate],
  );

  const state = useMemo(
    () => ({ viewTemplate, savedViewTemplate, isModified }),
    [viewTemplate, savedViewTemplate, isModified],
  );

  return (
    <ViewTemplateActionsProvider value={actions}>
      <ViewTemplateStateProvider value={state}>
        {children}
        <PreserveViewTemplate setIsModified={setIsModified} />
      </ViewTemplateStateProvider>
    </ViewTemplateActionsProvider>
  );
}

function getViewTemplateUpdates(
  savedViewTemplate: API.ViewTemplateFragment,
  viewTemplate: API.ViewTemplateFragment,
): Partial<EditableViewTemplate> {
  const editableSaved = getEditableViewTemplate(savedViewTemplate);
  const editableCurrent = getEditableViewTemplate(viewTemplate);
  const entries = Object.entries(editableSaved) as [keyof EditableViewTemplate, unknown][];
  return entries.reduce((memo: Partial<EditableViewTemplate>, [key, value]) => {
    if (!isEqual(editableCurrent[key], value)) {
      return { ...memo, [key]: value };
    } else {
      return memo;
    }
  }, {});
}
